import { AlertColor } from '@mui/material/Alert'
import { SnackbarOrigin } from '@mui/material/Snackbar'
import { Casing, TEXT_ROLE_CASING } from '@common/helpers/casing'
import { combineReducers } from 'redux'
import { IAction } from '../apiMiddleware'
import * as actions from './actions'
import { IMessageToast } from './actions'

/** Beyond this the stack becomes the thing covering your screen. */
const MAX_VISIBLE_MESSAGE_TOASTS = 3

interface INotificationState {
  isOpen: boolean
  type: AlertColor
  code: string
  message: string
  messagesList: string[]
  anchorOrigin: SnackbarOrigin
  autoHideDuration: number
  noShowMessage?: boolean
}

const notificationInitialState = {
  isOpen: false,
  type: 'success' as AlertColor,
  code: '',
  message: '',
  messagesList: [] as string[],
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'right',
  } as SnackbarOrigin,
  autoHideDuration: 7000,
}

export default combineReducers({
  notification(
    state: INotificationState = notificationInitialState,
    action: IAction,
  ): INotificationState {
    switch (action.type) {
      case actions.PUT_NOTIFICATION:
        return { ...state, ...(action.payload as Partial<INotificationState>) }
      case actions.REMOVE_NOTIFICATION:
        return notificationInitialState
      default:
        return state
    }
  },
  messageToasts(state: IMessageToast[] = [], action: IAction): IMessageToast[] {
    switch (action.type) {
      case actions.PUSH_MESSAGE_TOAST: {
        const toast = action.payload as IMessageToast
        // A resend or a duplicate socket frame must not stack the same message twice.
        if (state.some((existing) => existing.messageId === toast.messageId)) return state
        // Oldest fall off the top rather than growing a column down the screen — a
        // busy channel would otherwise cover the page it is notifying you about.
        return [...state, toast].slice(-MAX_VISIBLE_MESSAGE_TOASTS)
      }
      case actions.DISMISS_MESSAGE_TOAST: {
        const { messageId } = action.payload as { messageId: string }
        return state.filter((toast) => toast.messageId !== messageId)
      }
      default:
        return state
    }
  },
  title(state = '', action: IAction): string {
    switch (action.type) {
      case actions.CHANGE_PAGE_TITLE:
        return (action.payload as { title: string }).title
      default:
        return state
    }
  },
  titleCasing(state: Casing = TEXT_ROLE_CASING.pageTitle, action: IAction): Casing {
    switch (action.type) {
      case actions.CHANGE_PAGE_TITLE:
        return (action.payload as { casing: Casing }).casing
      default:
        return state
    }
  },
  pageSubtitle(state = '', action: IAction): string {
    switch (action.type) {
      case actions.CHANGE_PAGE_SUBTITLE:
        return (action.payload as { subtitle: string }).subtitle
      default:
        return state
    }
  },
  pageLogo(state = '', action: IAction): string {
    switch (action.type) {
      case actions.CHANGE_PAGE_LOGO:
        return (action.payload as { logo: string }).logo
      default:
        return state
    }
  },
  pageParent(
    state: actions.IPageParent | null = null,
    action: IAction,
  ): actions.IPageParent | null {
    switch (action.type) {
      case actions.CHANGE_PAGE_PARENT:
        return (action.payload as { parent: actions.IPageParent | null }).parent
      default:
        return state
    }
  },
  pageLogoSubject(
    state: actions.IPageLogoSubject | null = null,
    action: IAction,
  ): actions.IPageLogoSubject | null {
    switch (action.type) {
      case actions.CHANGE_PAGE_LOGO_SUBJECT:
        return (action.payload as { subject: actions.IPageLogoSubject | null }).subject
      default:
        return state
    }
  },
})
